import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "driver-filters-bar" }
const _hoisted_2 = { class: "row g-3" }
const _hoisted_3 = { class: "col-12 col-sm-6 col-lg-3" }
const _hoisted_4 = { class: "form-label fw-bold driver-filters-bar__label" }
const _hoisted_5 = { class: "driver-filters-bar__search" }
const _hoisted_6 = ["placeholder", "aria-label", "value"]
const _hoisted_7 = { class: "col-12 col-sm-6 col-lg-3" }
const _hoisted_8 = { class: "form-label fw-bold driver-filters-bar__label" }
const _hoisted_9 = ["aria-label", "value"]
const _hoisted_10 = { value: "" }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "col-12 col-sm-6 col-lg-3" }
const _hoisted_13 = { class: "form-label fw-bold driver-filters-bar__label" }
const _hoisted_14 = ["aria-label", "value"]
const _hoisted_15 = { value: "" }
const _hoisted_16 = ["value"]
const _hoisted_17 = { class: "col-12 col-sm-6 col-lg-3" }
const _hoisted_18 = { class: "form-label fw-bold driver-filters-bar__label" }
const _hoisted_19 = ["aria-label", "value", "disabled", "title"]
const _hoisted_20 = { value: "" }
const _hoisted_21 = ["value"]
const _hoisted_22 = {
  key: 0,
  class: "col-12 col-sm-6 col-lg-3"
}
const _hoisted_23 = { class: "form-label fw-bold driver-filters-bar__label" }
const _hoisted_24 = ["aria-label", "value"]
const _hoisted_25 = ["value"]
const _hoisted_26 = { class: "col-12 col-sm-6 col-lg-3" }
const _hoisted_27 = { class: "form-label fw-bold driver-filters-bar__label" }
const _hoisted_28 = ["aria-label", "value"]
const _hoisted_29 = { value: "" }
const _hoisted_30 = ["value"]
const _hoisted_31 = {
  key: 0,
  class: "mt-3"
}

import { computed, ref, watch } from 'vue'
import type { ActiveFilters } from '@/types/ActiveFilters'
import { useI18n } from 'vue-i18n'
import {
  currentBogotaPeriod,
  fallbackLabel as fallbackLabelHelper,
  periodLabel as periodLabelHelper,
  periodOptions as periodOptionsHelper,
} from '@/helpers/driverFilterLabels'

interface Props {
  filters: ActiveFilters
  search: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DriverFiltersBar',
  props: {
    filters: {},
    search: {}
  },
  emits: ["update:filters", "update:search"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit
const { t, locale } = useI18n()

const localSearch = ref(props.search)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

const statusValues: Array<'enabled' | 'disabled'> = ['enabled', 'disabled']
const paymentValues: Array<'monthly' | 'percentage'> = ['monthly', 'percentage']
const paymentStatusValues: Array<'paid' | 'pending'> = ['paid', 'pending']
const cannedInactiveDays = [1, 7, 30]

const searchPlaceholder = computed(() =>
  fallbackLabel('drivers.placeholders.search', 'Search')
)
const allLabel = computed(() => fallbackLabel('common.placeholders.all', 'All'))
const inactiveNoneLabel = computed(() =>
  fallbackLabel('drivers.filters.inactive_none', 'None')
)
const inactiveOptions = computed(() => {
  const values = new Set(cannedInactiveDays)
  const current = props.filters.inactiveDays
  if (current && current > 0) {
    values.add(current)
  }
  return Array.from(values).sort((a, b) => a - b)
})

const paymentStatusDisabled = computed(() => props.filters.paymentMode === 'percentage')
const paymentStatusHint = computed(() =>
  fallbackLabel(
    'drivers.filters.payment_status_percentage_hint',
    'Not applicable for percentage-paid drivers'
  )
)

const hasActiveFilters = computed(() =>
  Object.keys(props.filters).length > 0 || localSearch.value !== ''
)

const defaultPeriod = computed(() => currentBogotaPeriod())

const periodOptions = computed(() => periodOptionsHelper(props.filters.period))

watch(() => props.search, (value) => {
  localSearch.value = value
})

function onSearchInput(event: Event): void {
  const value = (event.target as HTMLInputElement).value
  localSearch.value = value
  if (debounceTimer !== null) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    emit('update:search', value)
  }, 300)
}

function fallbackLabel(key: string, fallback: string, params?: Record<string, number>): string {
  return fallbackLabelHelper(t, key, fallback, params)
}

function statusLabel(value: 'enabled' | 'disabled'): string {
  return fallbackLabel(`drivers.filters.status_values.${value}`, value)
}

function paymentLabel(value: 'monthly' | 'percentage'): string {
  return fallbackLabel(`drivers.filters.payment_values.${value}`, value)
}

function inactiveLabel(days: number): string {
  return fallbackLabel('drivers.filters.inactive_days', `${days}d`, { days })
}

function paymentStatusLabel(value: 'paid' | 'pending'): string {
  return fallbackLabel(`drivers.filters.payment_status_values.${value}`, value)
}

function periodLabel(period: string): string {
  return periodLabelHelper(period, locale.value)
}

function onStatusChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  const nextFilters = { ...props.filters }
  if (value === 'enabled' || value === 'disabled') {
    nextFilters.status = value
  } else {
    delete nextFilters.status
  }
  emit('update:filters', nextFilters)
}

function onPaymentChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  const nextFilters = { ...props.filters }
  if (value === 'monthly' || value === 'percentage') {
    nextFilters.paymentMode = value
  } else {
    delete nextFilters.paymentMode
  }
  if (nextFilters.paymentMode === 'percentage') {
    delete nextFilters.paymentStatus
    delete nextFilters.period
  }
  emit('update:filters', nextFilters)
}

function onPaymentStatusChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  const nextFilters = { ...props.filters }
  if (value === 'paid' || value === 'pending') {
    nextFilters.paymentStatus = value
    if (!nextFilters.period) {
      nextFilters.period = currentBogotaPeriod()
    }
  } else {
    delete nextFilters.paymentStatus
    delete nextFilters.period
  }
  emit('update:filters', nextFilters)
}

function onPeriodChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  const nextFilters = { ...props.filters }
  if (value) {
    nextFilters.period = value
  } else {
    delete nextFilters.period
  }
  emit('update:filters', nextFilters)
}

function onInactiveChange(event: Event): void {
  const value = Number((event.target as HTMLSelectElement).value)
  const nextFilters = { ...props.filters }
  if (value > 0) {
    nextFilters.inactiveDays = value
  } else {
    delete nextFilters.inactiveDays
  }
  emit('update:filters', nextFilters)
}

function onClearFilters(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  localSearch.value = ''
  emit('update:search', '')
  emit('update:filters', {})
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("label", _hoisted_4, _toDisplayString(fallbackLabel('drivers.filters.filter_search', 'Buscar')), 1),
        _createElementVNode("div", _hoisted_5, [
          _cache[0] || (_cache[0] = _createElementVNode("em", {
            class: "fas fa-search driver-filters-bar__search-icon",
            "aria-hidden": "true"
          }, null, -1)),
          _createElementVNode("input", {
            type: "text",
            class: "form-control form-control-sm driver-filters-bar__search-input",
            placeholder: searchPlaceholder.value,
            "aria-label": searchPlaceholder.value,
            value: localSearch.value,
            onInput: onSearchInput
          }, null, 40, _hoisted_6)
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("label", _hoisted_8, _toDisplayString(fallbackLabel('drivers.filters.filter_status', 'Status')), 1),
        _createElementVNode("select", {
          class: "form-select form-select-sm",
          name: "status",
          "aria-label": fallbackLabel('drivers.filters.filter_status', 'Status'),
          value: __props.filters.status ?? '',
          onChange: onStatusChange
        }, [
          _createElementVNode("option", _hoisted_10, _toDisplayString(allLabel.value), 1),
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statusValues, (value) => {
            return _createElementVNode("option", {
              key: value,
              value: value
            }, _toDisplayString(statusLabel(value)), 9, _hoisted_11)
          }), 64))
        ], 40, _hoisted_9)
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("label", _hoisted_13, _toDisplayString(fallbackLabel('drivers.filters.filter_payment', 'Payment')), 1),
        _createElementVNode("select", {
          class: "form-select form-select-sm",
          name: "paymentMode",
          "aria-label": fallbackLabel('drivers.filters.filter_payment', 'Payment'),
          value: __props.filters.paymentMode ?? '',
          onChange: onPaymentChange
        }, [
          _createElementVNode("option", _hoisted_15, _toDisplayString(allLabel.value), 1),
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(paymentValues, (value) => {
            return _createElementVNode("option", {
              key: value,
              value: value
            }, _toDisplayString(paymentLabel(value)), 9, _hoisted_16)
          }), 64))
        ], 40, _hoisted_14)
      ]),
      _createElementVNode("div", _hoisted_17, [
        _createElementVNode("label", _hoisted_18, _toDisplayString(fallbackLabel('drivers.filters.filter_payment_status', 'Payment status')), 1),
        _createElementVNode("select", {
          class: "form-select form-select-sm",
          name: "paymentStatus",
          "aria-label": fallbackLabel('drivers.filters.filter_payment_status', 'Payment status'),
          value: __props.filters.paymentStatus ?? '',
          disabled: paymentStatusDisabled.value,
          title: paymentStatusDisabled.value ? paymentStatusHint.value : undefined,
          onChange: onPaymentStatusChange
        }, [
          _createElementVNode("option", _hoisted_20, _toDisplayString(allLabel.value), 1),
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(paymentStatusValues, (value) => {
            return _createElementVNode("option", {
              key: value,
              value: value
            }, _toDisplayString(paymentStatusLabel(value)), 9, _hoisted_21)
          }), 64))
        ], 40, _hoisted_19)
      ]),
      (__props.filters.paymentStatus)
        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
            _createElementVNode("label", _hoisted_23, _toDisplayString(fallbackLabel('drivers.filters.filter_period', 'Period')), 1),
            _createElementVNode("select", {
              class: "form-select form-select-sm",
              name: "period",
              "aria-label": fallbackLabel('drivers.filters.filter_period', 'Period'),
              value: __props.filters.period ?? defaultPeriod.value,
              onChange: onPeriodChange
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(periodOptions.value, (value) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: value,
                  value: value
                }, _toDisplayString(periodLabel(value)), 9, _hoisted_25))
              }), 128))
            ], 40, _hoisted_24)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_26, [
        _createElementVNode("label", _hoisted_27, _toDisplayString(fallbackLabel('drivers.filters.filter_inactive', 'Inactivity')), 1),
        _createElementVNode("select", {
          class: "form-select form-select-sm",
          name: "inactiveDays",
          "aria-label": fallbackLabel('drivers.filters.filter_inactive', 'Inactivity'),
          value: __props.filters.inactiveDays?.toString() ?? '',
          onChange: onInactiveChange
        }, [
          _createElementVNode("option", _hoisted_29, _toDisplayString(inactiveNoneLabel.value), 1),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(inactiveOptions.value, (days) => {
            return (_openBlock(), _createElementBlock("option", {
              key: days,
              value: days.toString()
            }, _toDisplayString(inactiveLabel(days)), 9, _hoisted_30))
          }), 128))
        ], 40, _hoisted_28)
      ])
    ]),
    (hasActiveFilters.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_31, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-link btn-sm p-0 driver-filters-bar__clear",
            onClick: onClearFilters
          }, [
            _cache[1] || (_cache[1] = _createElementVNode("em", {
              class: "fas fa-times me-1",
              "aria-hidden": "true"
            }, null, -1)),
            _createTextVNode(_toDisplayString(fallbackLabel('common.actions.clear_filters', 'Limpiar filtros')), 1)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})